import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ClipboardList, Upload, AlertCircle, CheckCircle, LogOut, ExternalLink, FileText } from 'lucide-react';
import { clientDocuments, clientDocumentRequests } from '../utils/api';
import { useAuth } from '../context/AuthContext';

/**
 * IntakeRequired
 * ──────────────
 * Hard-gated page shown to a newly-signed-up client when the admin has
 * `block_signup_until_required_documents` enabled AND the client has at
 * least one REQUIRED pending document request (cloned from the intake
 * template at registration time).
 *
 * The user cannot leave this page until every required pending request
 * has been fulfilled. They can:
 *   - Upload a file against each required request to fulfill it
 *   - Log out (in case they want to abandon registration)
 *
 * Optional intake items are not shown here — they'll appear on the
 * regular My Documents tab after the user clears the gate. Only the
 * required ones block.
 *
 * Once the required count drops to zero, the "Continue" button enables;
 * pressing it clears the gate via AuthContext and routes to /.
 */

const TYPE_LABELS = {
  vaccine_record: 'Vaccine record',
  waiver: 'Waiver',
  contract: 'Contract',
  form_of_id: 'Form of ID',
  health_records: 'Health records',
  other: 'Other',
};

const IntakeRequired = () => {
  const { user, logout, clearIntakeGate } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingFor, setUploadingFor] = useState(null);

  const loadRequests = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const res = await clientDocumentRequests.list(user.id, { status: 'pending' });
      // Only required+pending items block the gate.
      setRequests((res.data || []).filter((r) => !!r.required));
    } catch (err) {
      console.error('Load intake requests error:', err);
      toast.error('Failed to load required documents');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  // Re-fetch when the tab regains focus. Form-kind items are filled out
  // in a new tab via the share-token link; the submit handler flips the
  // request to fulfilled server-side, but the gate page won't know until
  // it re-fetches. Hooking the focus event keeps the experience smooth
  // (no manual refresh needed).
  useEffect(() => {
    const onFocus = () => { loadRequests(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadRequests]);

  const handleUpload = async (req, file) => {
    setUploadingFor(req.id);
    try {
      await clientDocuments.upload(user.id, file, {
        documentType: req.document_type,
        label: req.title,
        requestId: req.id,
      });
      toast.success(`Uploaded: ${req.title}`);
      await loadRequests();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploadingFor(null);
    }
  };

  const handleContinue = () => {
    if (requests.length > 0) {
      toast.error('Please upload all required documents first.');
      return;
    }
    clearIntakeGate();
    navigate('/', { replace: true });
  };

  const handleLogout = () => {
    if (!window.confirm('Log out and abandon this signup? Your account will remain but you must come back to finish intake before using it.')) return;
    logout();
    navigate('/login', { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  const allDone = requests.length === 0;

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-3xl mx-auto px-4">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 text-primary-700 rounded-full mb-4">
            <ClipboardList size={32} />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome{user?.firstName ? `, ${user.firstName}` : ''}!
          </h1>
          <p className="text-gray-600 max-w-xl mx-auto">
            Before you can start using the app, your provider needs a few things from you.
            Please complete each item below. You can come back to this page if you need a break —
            just log back in.
          </p>
        </div>

        {allDone ? (
          <div className="card border-green-200 bg-green-50 text-center">
            <CheckCircle className="mx-auto text-green-600 mb-3" size={48} />
            <h2 className="text-xl font-bold text-green-900 mb-2">You're all set!</h2>
            <p className="text-green-800 mb-6">
              Every required item is done. You can now use the app.
            </p>
            <button
              onClick={handleContinue}
              className="btn btn-primary inline-flex items-center gap-2"
            >
              <span>Continue</span>
            </button>
          </div>
        ) : (
          <>
            <div className="card border-amber-200 bg-amber-50 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="text-amber-600 shrink-0 mt-0.5" size={20} />
                <div className="text-sm text-amber-900">
                  <strong>{requests.length}</strong> required document{requests.length === 1 ? '' : 's'} remaining.
                  Complete each one to continue. Documents may be image (JPG/PNG/HEIC) or PDF, up to 10 MB per file.
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {requests.map((req) => {
                const isForm = req.kind === 'form';
                return (
                  <div key={req.id} className="card">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{req.title}</h3>
                          {isForm ? (
                            <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-800 rounded inline-flex items-center gap-1">
                              <FileText size={12} /> Form{req.form_name ? `: ${req.form_name}` : ''}
                            </span>
                          ) : (
                            <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded">
                              {TYPE_LABELS[req.document_type] || req.document_type}
                            </span>
                          )}
                          <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded">Required</span>
                        </div>
                        {req.instructions && (
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{req.instructions}</p>
                        )}
                      </div>
                      {isForm ? (
                        req.form_share_token ? (
                          <a
                            href={`/forms/fill/${req.form_share_token}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-primary flex items-center gap-2 shrink-0"
                          >
                            <ClipboardList size={16} />
                            <span>Fill out form</span>
                            <ExternalLink size={12} />
                          </a>
                        ) : (
                          <span className="text-xs text-amber-700 italic shrink-0">
                            Share link missing — contact your provider
                          </span>
                        )
                      ) : (
                        <label className="btn btn-primary cursor-pointer flex items-center gap-2 shrink-0">
                          <Upload size={16} />
                          <span>{uploadingFor === req.id ? 'Uploading…' : 'Upload'}</span>
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*,application/pdf"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) handleUpload(req, f);
                              e.target.value = '';
                            }}
                            disabled={uploadingFor === req.id}
                          />
                        </label>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-start items-center mt-8">
              <button
                onClick={handleLogout}
                className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
              >
                <LogOut size={16} />
                <span>Log out and finish later</span>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default IntakeRequired;
